import { useState, useEffect } from "react";
import { useOutletContext } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Clock, Calendar, Plus, Trash2, Save, Settings, CalendarDays,
  RefreshCw, Check, AlertCircle, User, X, CheckCircle, XCircle,
  Users, CalendarCheck, CalendarX, Briefcase
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription
} from "@/components/ui/dialog";
import {
  Card, CardContent, CardDescription, CardHeader, CardTitle
} from "@/components/ui/card";
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue
} from "@/components/ui/select";
import {
  Tabs, TabsContent, TabsList, TabsTrigger
} from "@/components/ui/tabs";
import { Label } from "@/components/ui/label";

const BACKEND_URL = ((typeof window !== 'undefined' && window.location.hostname.includes('seatpilot.co.uk')) ? 'https://seatpilot.co.uk' : (process.env.REACT_APP_BACKEND_URL || 'https://seatpilot.co.uk'));
const API = `${BACKEND_URL}/api`;

const DAYS = ["monday", "tuesday", "wednesday", "thursday", "friday", "saturday", "sunday"];
const DAY_LABELS = {
  monday: "Monday", tuesday: "Tuesday", wednesday: "Wednesday",
  thursday: "Thursday", friday: "Friday", saturday: "Saturday", sunday: "Sunday"
};

const TIMEZONES = [
  "Europe/London", "Europe/Paris", "Europe/Berlin", "America/New_York",
  "America/Los_Angeles", "America/Chicago", "Asia/Tokyo", "Asia/Singapore",
  "Australia/Sydney", "UTC"
];

export default function BackofficeAvailability() {
  const { user, theme } = useOutletContext();
  const isDark = theme === 'dark';
  const isSuperAdmin = user?.is_super_admin || user?.role === 'super_admin';

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [activeTab, setActiveTab] = useState("my-availability");
  
  // My Availability
  const [availability, setAvailability] = useState(null);
  const [allStaffAvailability, setAllStaffAvailability] = useState([]);
  
  // Holiday Request
  const [showHolidayDialog, setShowHolidayDialog] = useState(false);
  const [newHoliday, setNewHoliday] = useState({
    start_date: "", end_date: "", reason: ""
  });
  
  // Holiday Requests (Admin)
  const [holidayRequests, setHolidayRequests] = useState([]);
  const [requestFilter, setRequestFilter] = useState("pending");
  const [processingRequest, setProcessingRequest] = useState(null);
  const [rejectNotes, setRejectNotes] = useState("");
  const [showRejectDialog, setShowRejectDialog] = useState(false);
  const [selectedRequest, setSelectedRequest] = useState(null);
  
  // Holiday Allowances (Admin)
  const [allowances, setAllowances] = useState([]);
  const [internalStaff, setInternalStaff] = useState([]);
  const [showAllowanceDialog, setShowAllowanceDialog] = useState(false);
  const [showAddStaffDialog, setShowAddStaffDialog] = useState(false);
  const [editingAllowance, setEditingAllowance] = useState(null);
  const [allowanceForm, setAllowanceForm] = useState({
    annual_allowance: 25,
    carry_over_limit: 5
  });
  const [selectedStaffId, setSelectedStaffId] = useState("");

  useEffect(() => {
    fetchData();
  }, []);
const fetchData = async () => {
    setLoading(true);
    await Promise.all([
      fetchAvailability(),
      fetchAllStaffAvailability(),
      isSuperAdmin && fetchHolidayRequests(),
      isSuperAdmin && fetchAllowances()
    ]);
    setLoading(false);
  };

  const fetchAvailability = async () => {
    try {
      const res = await axios.get(`${API}/activity/staff/availability`, { withCredentials: true });
      setAvailability(res.data.availability);
    } catch (error) {
      console.error("Failed to load availability");
    }
  };

  const fetchAllStaffAvailability = async () => {
    try {
      const res = await axios.get(`${API}/activity/admin/staff/availability`, { withCredentials: true });
      setAllStaffAvailability(res.data.staff || []);
    } catch (error) {
      console.error("Failed to load all staff availability");
    }
  };

  const fetchHolidayRequests = async () => {
    try {
      const res = await axios.get(`${API}/activity/admin/holiday-requests?status=${requestFilter}`, { withCredentials: true });
      setHolidayRequests(res.data.requests || []);
    } catch (error) {
      console.error("Failed to load holiday requests");
    }
  };

  const fetchAllowances = async () => {
    try {
      const res = await axios.get(`${API}/activity/admin/holiday-allowances`, { withCredentials: true });
      setAllowances(res.data.allowances || []);
      // Also get the staff list for "Add Staff" functionality
      if (res.data.staff) {
        setInternalStaff(res.data.staff);
      }
    } catch (error) {
      console.error("Failed to load allowances");
    }
  };

  useEffect(() => {
    if (isSuperAdmin) {
      fetchHolidayRequests();
    }
  }, [requestFilter]);
const handleSaveAvailability = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/activity/staff/availability`, {
        working_hours: availability.working_hours,
        max_tickets_per_day: availability.max_tickets_per_day,
        is_available: availability.is_available,
        auto_assign_enabled: availability.auto_assign_enabled
      }, { withCredentials: true });
      toast.success("Availability settings saved");
    } catch (error) {
      toast.error("Failed to save settings");
    } finally {
      setSaving(false);
    }
  };

  const handleAddHoliday = async () => {
    if (!newHoliday.start_date || !newHoliday.end_date) {
      toast.error("Please select start and end dates");
      return;
    }
    try {
      const res = await axios.post(`${API}/activity/staff/holidays`, newHoliday, { withCredentials: true });
      if (res.data.requires_approval) {
        toast.success("Holiday request submitted for approval");
      } else {
        toast.success("Holiday added");
      }
      setShowHolidayDialog(false);
      setNewHoliday({ start_date: "", end_date: "", reason: "" });
      fetchAvailability();
    } catch (error) {
      toast.error("Failed to add holiday");
    }
  };

  const handleDeleteHoliday = async (holidayId) => {
    try {
      await axios.delete(`${API}/activity/staff/holidays/${holidayId}`, { withCredentials: true });
      toast.success("Holiday removed");
      fetchAvailability();
    } catch (error) {
      toast.error("Failed to remove holiday");
    }
  };

  const handleProcessRequest = async (requestId, action, notes = "") => {
    setProcessingRequest(requestId);
    try {
      await axios.post(`${API}/activity/admin/holiday-requests/${requestId}`, {
        action,
        notes
      }, { withCredentials: true });
      toast.success(`Holiday request ${action}d`);
      fetchHolidayRequests();
      fetchAllStaffAvailability();
      setShowRejectDialog(false);
      setRejectNotes("");
    } catch (error) {
      toast.error(`Failed to ${action} request`);
    } finally {
      setProcessingRequest(null);
    }
  };

  const handleUpdateAllowance = async () => {
    if (!editingAllowance) return;
    try {
      await axios.put(`${API}/activity/admin/holiday-allowances/${editingAllowance.user_id}`, {
        user_id: editingAllowance.user_id,
        annual_allowance: allowanceForm.annual_allowance,
        carry_over_limit: allowanceForm.carry_over_limit,
        year: new Date().getFullYear()
      }, { withCredentials: true });
      toast.success("Holiday allowance updated");
      setShowAllowanceDialog(false);
      fetchAllowances();
    } catch (error) {
      toast.error("Failed to update allowance");
    }
  };

  const updateWorkingHours = (day, field, value) => {
    setAvailability(prev => ({
      ...prev,
      working_hours: {
        ...prev.working_hours,
        [day]: { ...prev.working_hours?.[day], [field]: value }
      }
    }));
  };

  const formatDate = (dateStr) => {
    if (!dateStr) return '-';
    return new Date(dateStr).toLocaleDateString('en-GB', {
      day: 'numeric', month: 'short', year: 'numeric'
    });
  };

  const calculateDays = (start, end) => {
    if (!start || !end) return 0;
    const startDate = new Date(start);
    const endDate = new Date(end);
    return Math.ceil((endDate - startDate) / (1000 * 60 * 60 * 24)) + 1;
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[400px]">
        <RefreshCw className="h-8 w-8 animate-spin text-violet-500" />
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex justify-between items-center">
        <div>
          <h1 className={`text-2xl font-bold flex items-center gap-2 ${isDark ? 'text-white' : 'text-gray-900'}`}>
            <Clock className="h-6 w-6 text-violet-500" />
            Staff Availability & Holidays
          </h1>
          <p className={`mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
            Manage working hours, time-off requests, and holiday allowances
          </p>
        </div>
        <Button onClick={fetchData} variant="outline" size="sm">
          <RefreshCw className="w-4 h-4 mr-2" /> Refresh
        </Button>
      </div>

      {/* Tabs */}
      <Tabs value={activeTab} onValueChange={setActiveTab}>
        <TabsList className={`grid ${isSuperAdmin ? 'grid-cols-4' : 'grid-cols-2'} w-full max-w-2xl`}>
          <TabsTrigger value="my-availability">
            <Clock className="w-4 h-4 mr-2" /> My Availability
          </TabsTrigger>
          <TabsTrigger value="my-holidays">
            <Calendar className="w-4 h-4 mr-2" /> My Holidays
          </TabsTrigger>
          {isSuperAdmin && (
            <>
              <TabsTrigger value="requests">
                <CalendarCheck className="w-4 h-4 mr-2" /> Requests
                {holidayRequests.filter(r => r.status === 'pending').length > 0 && (
                  <span className="ml-1 px-1.5 py-0.5 bg-red-500 text-white text-xs rounded-full">
                    {holidayRequests.filter(r => r.status === 'pending').length}
                  </span>
                )}
              </TabsTrigger>
              <TabsTrigger value="allowances">
                <Briefcase className="w-4 h-4 mr-2" /> Allowances
              </TabsTrigger>
            </>
          )}
        </TabsList>

        {/* My Availability Tab */}
        <TabsContent value="my-availability" className="space-y-6">
          <div className="flex justify-end">
            <Button onClick={handleSaveAvailability} disabled={saving}>
              {saving ? <RefreshCw className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />}
              Save Changes
            </Button>
          </div>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
            {/* Working Hours */}
            <Card className={`lg:col-span-2 ${isDark ? 'bg-slate-800 border-slate-700' : ''}`}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                  <CalendarDays className="h-5 w-5" />
                  Working Hours
                </CardTitle>
                <CardDescription>Set your regular working schedule</CardDescription>
              </CardHeader>
              <CardContent className="space-y-4">
                {/* Timezone */}
                <div className="flex items-center gap-4 pb-4 border-b">
                  <Label className="w-24">Timezone</Label>
                  <Select
                    value={availability?.working_hours?.timezone || "Europe/London"}
                    onValueChange={(v) => setAvailability(prev => ({
                      ...prev,
                      working_hours: { ...prev?.working_hours, timezone: v }
                    }))}
                  >
                    <SelectTrigger className="w-48">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      {TIMEZONES.map(tz => (
                        <SelectItem key={tz} value={tz}>{tz}</SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>

                {/* Days */}
                {DAYS.map(day => {
                  const dayData = availability?.working_hours?.[day] || { start: "09:00", end: "17:00", enabled: true };
                  return (
                    <div key={day} className="flex items-center gap-4">
                      <Switch
                        checked={dayData.enabled}
                        onCheckedChange={(checked) => updateWorkingHours(day, "enabled", checked)}
                      />
                      <span className={`w-24 font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                        {DAY_LABELS[day]}
                      </span>
                      {dayData.enabled ? (
                        <>
                          <Input
                            type="time"
                            value={dayData.start}
                            onChange={(e) => updateWorkingHours(day, "start", e.target.value)}
                            className="w-32"
                          />
                          <span className={isDark ? 'text-slate-400' : 'text-gray-400'}>to</span>
                          <Input
                            type="time"
                            value={dayData.end}
                            onChange={(e) => updateWorkingHours(day, "end", e.target.value)}
                            className="w-32"
                          />
                        </>
                      ) : (
                        <span className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>Day off</span>
                      )}
                    </div>
                  );
                })}
              </CardContent>
            </Card>

            {/* Settings */}
            <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
              <CardHeader>
                <CardTitle className={`flex items-center gap-2 ${isDark ? 'text-white' : ''}`}>
                  <Settings className="h-5 w-5" />
                  Settings
                </CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Available for tickets</p>
                    <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Accept new assignments</p>
                  </div>
                  <Switch
                    checked={availability?.is_available !== false}
                    onCheckedChange={(checked) => setAvailability(prev => ({ ...prev, is_available: checked }))}
                  />
                </div>
                <div className="flex items-center justify-between">
                  <div>
                    <p className={`font-medium ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>Auto-assign enabled</p>
                    <p className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-500'}`}>Include in auto-assignment</p>
                  </div>
                  <Switch
                    checked={availability?.auto_assign_enabled !== false}
                    onCheckedChange={(checked) => setAvailability(prev => ({ ...prev, auto_assign_enabled: checked }))}
                  />
                </div>
                <div className="pt-2">
                  <Label className={isDark ? 'text-slate-300' : ''}>Max tickets per day</Label>
                  <Input
                    type="number"
                    value={availability?.max_tickets_per_day || 20}
                    onChange={(e) => setAvailability(prev => ({ ...prev, max_tickets_per_day: parseInt(e.target.value) }))}
                    className="mt-1"
                  />
                </div>
              </CardContent>
            </Card>
          </div>
        </TabsContent>

        {/* My Holidays Tab */}
        <TabsContent value="my-holidays" className="space-y-6">
          <div className="flex justify-between items-center">
            <div>
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>My Holidays & Time Off</h3>
              <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                Request time off - pending requests need admin approval
              </p>
            </div>
            <Button onClick={() => setShowHolidayDialog(true)}>
              <Plus className="w-4 h-4 mr-2" /> Request Holiday
            </Button>
          </div>

          <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
            <CardContent className="pt-6">
              {(!availability?.holidays || availability.holidays.length === 0) ? (
                <div className="text-center py-8">
                  <Calendar className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
                  <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No holidays scheduled</p>
                </div>
              ) : (
                <div className="space-y-3">
                  {availability.holidays.map(holiday => (
                    <div 
                      key={holiday.holiday_id}
                      className={`flex items-center justify-between p-4 rounded-lg border ${
                        isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'
                      }`}
                    >
                      <div className="flex items-center gap-4">
                        <div className={`p-2 rounded-lg ${
                          holiday.status === 'approved' ? 'bg-green-100 text-green-600' :
                          holiday.status === 'pending' ? 'bg-amber-100 text-amber-600' :
                          'bg-red-100 text-red-600'
                        }`}>
                          {holiday.status === 'approved' ? <CheckCircle className="w-5 h-5" /> :
                           holiday.status === 'pending' ? <Clock className="w-5 h-5" /> :
                           <XCircle className="w-5 h-5" />}
                        </div>
                        <div>
                          <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                            {formatDate(holiday.start_date)} - {formatDate(holiday.end_date)}
                          </p>
                          <div className="flex items-center gap-2 mt-1">
                            <span className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                              {calculateDays(holiday.start_date, holiday.end_date)} days
                            </span>
                            {holiday.reason && (
                              <span className={`text-sm ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                                • {holiday.reason}
                              </span>
                            )}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center gap-2">
                        <span className={`px-2 py-1 rounded text-xs font-medium ${
                          holiday.status === 'approved' ? 'bg-green-100 text-green-700' :
                          holiday.status === 'pending' ? 'bg-amber-100 text-amber-700' :
                          'bg-red-100 text-red-700'
                        }`}>
                          {holiday.status}
                        </span>
                        {holiday.status === 'pending' && (
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={() => handleDeleteHoliday(holiday.holiday_id)}
                          >
                            <Trash2 className="w-4 h-4 text-red-500" />
                          </Button>
                        )}
                      </div>
                    </div>
                  ))}
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        {/* Holiday Requests Tab (Admin Only) */}
        {isSuperAdmin && (
          <TabsContent value="requests" className="space-y-6">
            <div className="flex justify-between items-center">
              <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Holiday Requests</h3>
              <Select value={requestFilter} onValueChange={setRequestFilter}>
                <SelectTrigger className="w-40">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                  <SelectItem value="rejected">Rejected</SelectItem>
                  <SelectItem value="all">All</SelectItem>
                </SelectContent>
              </Select>
            </div>

            <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
              <CardContent className="pt-6">
                {holidayRequests.length === 0 ? (
                  <div className="text-center py-8">
                    <CalendarCheck className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
                    <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No {requestFilter} requests</p>
                  </div>
                ) : (
                  <div className="space-y-3">
                    {holidayRequests.map(req => (
                      <div 
                        key={req.request_id}
                        className={`p-4 rounded-lg border ${isDark ? 'bg-slate-700/50 border-slate-600' : 'bg-gray-50 border-gray-200'}`}
                      >
                        <div className="flex items-start justify-between">
                          <div>
                            <div className="flex items-center gap-2">
                              <User className="w-4 h-4 text-violet-500" />
                              <p className={`font-medium ${isDark ? 'text-white' : 'text-gray-900'}`}>
                                {req.user_name || req.user_email}
                              </p>
                            </div>
                            <p className={`mt-1 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                              {formatDate(req.start_date)} - {formatDate(req.end_date)} 
                              <span className={`ml-2 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                ({calculateDays(req.start_date, req.end_date)} days)
                              </span>
                            </p>
                            {req.reason && (
                              <p className={`text-sm mt-1 ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                                Reason: {req.reason}
                              </p>
                            )}
                            <p className={`text-xs mt-2 ${isDark ? 'text-slate-500' : 'text-gray-400'}`}>
                              Requested: {formatDate(req.created_at)}
                            </p>
                          </div>
                          <div className="flex items-center gap-2">
                            {req.status === 'pending' ? (
                              <>
                                <Button
                                  size="sm"
                                  className="bg-green-600 hover:bg-green-700"
                                  onClick={() => handleProcessRequest(req.request_id, 'approve')}
                                  disabled={processingRequest === req.request_id}
                                >
                                  <Check className="w-4 h-4 mr-1" /> Approve
                                </Button>
                                <Button
                                  size="sm"
                                  variant="destructive"
                                  onClick={() => {
                                    setSelectedRequest(req);
                                    setShowRejectDialog(true);
                                  }}
                                  disabled={processingRequest === req.request_id}
                                >
                                  <X className="w-4 h-4 mr-1" /> Reject
                                </Button>
                              </>
                            ) : (
                              <span className={`px-2 py-1 rounded text-xs font-medium ${
                                req.status === 'approved' ? 'bg-green-100 text-green-700' : 'bg-red-100 text-red-700'
                              }`}>
                                {req.status}
                              </span>
                            )}
                          </div>
                        </div>
                        {req.notes && (
                          <p className={`text-sm mt-2 p-2 rounded ${isDark ? 'bg-slate-600' : 'bg-gray-100'}`}>
                            Admin note: {req.notes}
                          </p>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}

        {/* Holiday Allowances Tab (Admin Only) */}
        {isSuperAdmin && (
          <TabsContent value="allowances" className="space-y-6">
            <div className="flex justify-between items-center">
              <div>
                <h3 className={`text-lg font-semibold ${isDark ? 'text-white' : 'text-gray-900'}`}>Holiday Allowances</h3>
                <p className={`text-sm ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                  Configure annual holiday allowance for internal staff members
                </p>
              </div>
              <Button onClick={() => setShowAddStaffDialog(true)}>
                <Plus className="w-4 h-4 mr-2" /> Add Staff Allowance
              </Button>
            </div>

            <Card className={isDark ? 'bg-slate-800 border-slate-700' : ''}>
              <CardContent className="pt-6">
                {allowances.length === 0 ? (
                  <div className="text-center py-8">
                    <Briefcase className={`w-12 h-12 mx-auto mb-2 ${isDark ? 'text-slate-600' : 'text-gray-300'}`} />
                    <p className={isDark ? 'text-slate-400' : 'text-gray-500'}>No staff members found</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className={`border-b ${isDark ? 'border-slate-700' : 'border-gray-200'}`}>
                          <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Staff Member</th>
                          <th className={`text-left py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Role</th>
                          <th className={`text-center py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Allowance</th>
                          <th className={`text-center py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Used</th>
                          <th className={`text-center py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Remaining</th>
                          <th className={`text-center py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Carry Over</th>
                          <th className={`text-right py-3 px-4 text-sm font-medium ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {allowances.map(a => (
                          <tr key={a.user_id} className={`border-b ${isDark ? 'border-slate-700 hover:bg-slate-700/50' : 'border-gray-100 hover:bg-gray-50'}`}>
                            <td className={`py-3 px-4 ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              <p className="font-medium">{a.user_name || a.user_email}</p>
                              <p className={`text-xs ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>{a.user_email}</p>
                            </td>
                            <td className={`py-3 px-4 ${isDark ? 'text-slate-300' : 'text-gray-700'}`}>
                              <span className="capitalize">{a.role?.replace('_', ' ')}</span>
                            </td>
                            <td className={`py-3 px-4 text-center font-bold ${isDark ? 'text-white' : 'text-gray-900'}`}>
                              {a.annual_allowance} days
                            </td>
                            <td className={`py-3 px-4 text-center ${isDark ? 'text-amber-400' : 'text-amber-600'}`}>
                              {a.days_used || 0} days
                            </td>
                            <td className={`py-3 px-4 text-center font-bold ${a.days_remaining > 5 ? 'text-green-500' : 'text-red-500'}`}>
                              {a.days_remaining || a.annual_allowance} days
                            </td>
                            <td className={`py-3 px-4 text-center ${isDark ? 'text-slate-400' : 'text-gray-500'}`}>
                              Max {a.carry_over_limit || 5} days
                            </td>
                            <td className="py-3 px-4 text-right">
                              <Button
                                variant="outline"
                                size="sm"
                                onClick={() => {
                                  setEditingAllowance(a);
                                  setAllowanceForm({
                                    annual_allowance: a.annual_allowance || 25,
                                    carry_over_limit: a.carry_over_limit || 5
                                  });
                                  setShowAllowanceDialog(true);
                                }}
                              >
                                Edit
                              </Button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </CardContent>
            </Card>
          </TabsContent>
        )}
      </Tabs>

      {/* Add Holiday Dialog */}
      <Dialog open={showHolidayDialog} onOpenChange={setShowHolidayDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Request Holiday</DialogTitle>
            <DialogDescription>
              {isSuperAdmin 
                ? "Your request will be approved automatically."
                : "Your request will be sent to admin for approval."}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Start Date</Label>
                <Input
                  type="date"
                  value={newHoliday.start_date}
                  onChange={(e) => setNewHoliday({...newHoliday, start_date: e.target.value})}
                />
              </div>
              <div>
                <Label>End Date</Label>
                <Input
                  type="date"
                  value={newHoliday.end_date}
                  onChange={(e) => setNewHoliday({...newHoliday, end_date: e.target.value})}
                />
              </div>
            </div>
            {newHoliday.start_date && newHoliday.end_date && (
              <p className="text-sm text-violet-600 font-medium">
                {calculateDays(newHoliday.start_date, newHoliday.end_date)} day(s) requested
              </p>
            )}
            <div>
              <Label>Reason (Optional)</Label>
              <Textarea
                value={newHoliday.reason}
                onChange={(e) => setNewHoliday({...newHoliday, reason: e.target.value})}
                placeholder="e.g., Family vacation, Personal time off"
                rows={2}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowHolidayDialog(false)}>Cancel</Button>
            <Button onClick={handleAddHoliday}>Submit Request</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reject Dialog */}
      <Dialog open={showRejectDialog} onOpenChange={setShowRejectDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Reject Holiday Request</DialogTitle>
            <DialogDescription>
              Rejecting request from {selectedRequest?.user_name} for {formatDate(selectedRequest?.start_date)} - {formatDate(selectedRequest?.end_date)}
            </DialogDescription>
          </DialogHeader>
          <div className="py-4">
            <Label>Reason for rejection (Optional)</Label>
            <Textarea
              value={rejectNotes}
              onChange={(e) => setRejectNotes(e.target.value)}
              placeholder="e.g., Too many staff already off during this period"
              rows={3}
            />
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowRejectDialog(false)}>Cancel</Button>
            <Button
              variant="destructive"
              onClick={() => handleProcessRequest(selectedRequest?.request_id, 'reject', rejectNotes)}
            >
              Reject Request
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Edit Allowance Dialog */}
      <Dialog open={showAllowanceDialog} onOpenChange={setShowAllowanceDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Holiday Allowance</DialogTitle>
            <DialogDescription>
              Configure allowance for {editingAllowance?.user_name || editingAllowance?.user_email}
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Annual Allowance (Days)</Label>
              <Input
                type="number"
                value={allowanceForm.annual_allowance}
                onChange={(e) => setAllowanceForm({...allowanceForm, annual_allowance: parseInt(e.target.value) || 0})}
                min={0}
                max={365}
              />
            </div>
            <div>
              <Label>Max Carry Over (Days)</Label>
              <Input
                type="number"
                value={allowanceForm.carry_over_limit}
                onChange={(e) => setAllowanceForm({...allowanceForm, carry_over_limit: parseInt(e.target.value) || 0})}
                min={0}
                max={30}
              />
              <p className="text-xs text-gray-500 mt-1">Maximum days that can be carried over to next year</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowAllowanceDialog(false)}>Cancel</Button>
            <Button onClick={handleUpdateAllowance}>Save Changes</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Add Staff Allowance Dialog */}
      <Dialog open={showAddStaffDialog} onOpenChange={setShowAddStaffDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Staff Holiday Allowance</DialogTitle>
            <DialogDescription>
              Select an internal staff member and configure their holiday allowance
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div>
              <Label>Select Staff Member</Label>
              <Select value={selectedStaffId} onValueChange={setSelectedStaffId}>
                <SelectTrigger>
                  <SelectValue placeholder="Choose a staff member..." />
                </SelectTrigger>
                <SelectContent>
                  {internalStaff
                    .filter(s => !allowances.find(a => a.user_id === s.user_id))
                    .map(staff => (
                      <SelectItem key={staff.user_id} value={staff.user_id}>
                        <div className="flex items-center gap-2">
                          <span>{staff.name || staff.email}</span>
                          <span className="text-xs text-gray-400 capitalize">({staff.role?.replace('_', ' ')})</span>
                        </div>
                      </SelectItem>
                    ))}
                </SelectContent>
              </Select>
              {internalStaff.filter(s => !allowances.find(a => a.user_id === s.user_id)).length === 0 && (
                <p className="text-sm text-amber-600 mt-2">All internal staff members already have allowances configured.</p>
              )}
            </div>
            <div>
              <Label>Annual Allowance (Days)</Label>
              <Input
                type="number"
                value={allowanceForm.annual_allowance}
                onChange={(e) => setAllowanceForm({...allowanceForm, annual_allowance: parseInt(e.target.value) || 0})}
                min={0}
                max={365}
              />
            </div>
            <div>
              <Label>Max Carry Over (Days)</Label>
              <Input
                type="number"
                value={allowanceForm.carry_over_limit}
                onChange={(e) => setAllowanceForm({...allowanceForm, carry_over_limit: parseInt(e.target.value) || 0})}
                min={0}
                max={30}
              />
              <p className="text-xs text-gray-500 mt-1">Maximum days that can be carried over to next year</p>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => {
              setShowAddStaffDialog(false);
              setSelectedStaffId("");
            }}>Cancel</Button>
            <Button 
              onClick={async () => {
                if (!selectedStaffId) {
                  toast.error("Please select a staff member");
                  return;
                }
                try {
                  await axios.put(`${API}/activity/admin/holiday-allowances/${selectedStaffId}`, {
                    user_id: selectedStaffId,
                    annual_allowance: allowanceForm.annual_allowance,
                    carry_over_limit: allowanceForm.carry_over_limit,
                    year: new Date().getFullYear()
                  }, { withCredentials: true });
                  toast.success("Holiday allowance created");
                  setShowAddStaffDialog(false);
                  setSelectedStaffId("");
                  fetchAllowances();
                } catch (error) {
                  toast.error("Failed to create allowance");
                }
              }}
              disabled={!selectedStaffId}
            >
              Create Allowance
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
